'use client'

import { useState } from 'react'
import Link from 'next/link'
import type { ListingSummary } from '@/lib/feedbridge/types'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ListingGrid } from '@/components/listings/ListingGrid'
import { renderHeading } from '@/utilities/renderHeading'

export interface TabData {
  label: string
  listings: ListingSummary[]
  featuresMap: Record<string, Record<string, unknown>>
  underOfferMap: Record<string, boolean>
  thumbnailMap?: Record<string, string>
  thumbnailsMap?: Record<string, string[]>
  linkLabel?: string | null
  linkHref?: string | null
}

interface TabbedListingsProps {
  heading?: string | null
  tabs: TabData[]
}

// Figma renders the listings heading as a single Merriweather paragraph with a regular run
// and an italic one (4022:82069 — "Your Next Move, " + italic "Right Here"). The CMS
// `heading` field is plain text, so a segment wrapped in asterisks is rendered italic —
// the same convention TrustIndicators and the CTA block already use. Headings without
// asterisks are unaffected.

export function TabbedListings({ heading, tabs }: TabbedListingsProps) {
  const [activeIndex, setActiveIndex] = useState(0)
  const activeTab = tabs[activeIndex]

  if (!tabs.length) return null

  return (
    // Section padding — Figma Frame 453: desktop 4022:82066 and tablet 4022:94548 are
    // section/md 96 top+bottom; mobile 4022:100110 is section/sm 64 (2899 = 64 + 2771 + 64).
    //
    // `asChild` merges the Tabs root INTO this section container rather than adding a
    // wrapper element, so the migration is DOM-neutral apart from the tab row itself.
    <Tabs
      asChild
      onValueChange={(value) => setActiveIndex(Number(value))}
      value={String(activeIndex)}
    >
      <div className="mx-auto max-w-[var(--container-content)] px-[var(--spacing-container)] py-[var(--spacing-section-sm)] md:py-[var(--spacing-section-md)]">
        {/* Header — Figma Frame 606: heading then [tabs][View All] row 24px below the
            heading. Header→grid gap is 64 on desktop (4022:82067: 104→168) and tablet
            (4022:94549: 96→160) but 48 on mobile (4022:100111: 128→176). */}
        <div className="flex flex-col gap-6 mb-12 md:mb-16">
          {heading && (
            <h2 className="font-display text-display-3xl font-normal text-brand">
              {renderHeading(heading)}
            </h2>
          )}

          {/* Tab control + View All — Figma Frame 694 (4022:82070): tabs (283×24) then
              button (67×24) at 8px gap, items-start. */}
          <div className="flex items-start gap-2 flex-wrap">
            {/* Tab control — canonical Tabs (Figma tab 2198:41383 / tabs 2198:41426,
                instance 4022:82071): muted track #e0dfe0 radius sm, tabs h-6 px-2 py-1
                Lato 400 14/16 #000, active = accent #8cc640 + shadow-sm. Caps
                ("FOR SALE", "SOLD", "FOR RENT", "LEASED") are an instance-level choice,
                not part of the component, so `uppercase` is applied here. Previously
                hand-rolled with a literal rgba shadow and no keyboard navigation. */}
            <TabsList aria-label="Featured listing type">
              {tabs.map((tab, i) => (
                <TabsTrigger className="uppercase" key={i} value={String(i)}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {/* "View All" — canonical Button, Figma outline type / MINI size (I2198:34629 via
                4022:82072 / 4022:94554 / 4022:100116, all 67×24): 1px theme/border, transparent
                fill, h-6 px-2, radius sm, shadow-xs, Body/bodySmAlt 14/16. Mini keeps the whole
                controls row at 24px, which is what the Figma header block heights assume. */}
            {activeTab?.linkLabel && activeTab?.linkHref && (
              <Button asChild className="shrink-0" size="mini" variant="outline">
                <Link href={activeTab.linkHref}>{activeTab.linkLabel}</Link>
              </Button>
            )}
          </div>
        </div>

        {/* Listing grid — one panel per tab; TabsContent unmounts the inactive ones, so
            exactly one grid renders, as before. */}
        {tabs.map((tab, i) => (
          <TabsContent key={i} value={String(i)}>
            {tab.listings.length === 0 ? (
              <div className="py-20 flex flex-col items-center justify-center gap-4 text-center">
                <p className="text-muted-foreground text-base font-normal">
                  No properties to show right now — check back soon.
                </p>
                {tab.linkHref && (
                  <Button asChild variant="outline">
                    <Link href={tab.linkHref}>Browse all properties</Link>
                  </Button>
                )}
              </div>
            ) : (
              <ListingGrid
                listings={tab.listings}
                featuresMap={tab.featuresMap}
                underOfferMap={tab.underOfferMap}
                thumbnailMap={tab.thumbnailMap}
                thumbnailsMap={tab.thumbnailsMap}
              />
            )}
          </TabsContent>
        ))}
      </div>
    </Tabs>
  )
}
